import React from 'react';
import ChartRadial from '@/components/charts/RadialNew';
import Header1 from '@/imgs/AssetAllocation/header1.png';
import Header2 from '@/imgs/AssetAllocation/header2.png';
import { extractNumber } from '@/lib/utils';
import Summary from '@/components/Summary';
// 根据类型映射颜色
const getTypeColor = (type: string) => {
  switch (type) {
    case '进取投资':
      return '#5959EB';
    case '稳健投资':
      return '#FF7B52';
    case '现金管理':
      return '#EB2F96';
    default:
      return '#8C8C8C';
  }
};

// 根据风险等级获取标签颜色
const getRiskLevelColor = (level: string) => {
  switch (level) {
    case 'R1':
      return 'green';
    case 'R2':
      return 'blue';
    case 'R3':
      return 'orange';
    case 'R4':
      return 'red';
    default:
      return 'gray';
  }
};
// 临时内联组件，避免导入问题
const HeaderSection = () => (
  <div className="mb-3 mt-6 flex items-center justify-between">
    <div className="flex items-center gap-2">
      <div className="flex h-6 w-6 items-center justify-center rounded">
        <img src={Header1} alt="header1" className="h-5 w-5" />
      </div>
      <span className="text-base font-semibold text-black">战略资产配置方案</span>
    </div>
    <div className="flex items-center gap-2">
      <button className="flex items-center gap-1 rounded bg-gradient-to-r from-purple-600 to-pink-600 px-2 py-1 text-sm text-white">
        <span>发送</span>
      </button>
      <button className="flex items-center gap-1 rounded border border-[#5959EB] bg-white px-2 py-1 text-sm text-[#5959EB]">
        <span>导出</span>
      </button>
    </div>
  </div>
);

const PerformanceTable = () => (
  <div className="rounded-lg bg-white p-3">
    <div className="mb-3 flex items-center gap-2">
      <span className="text-sm font-semibold text-[#1F1F1F]">组合表现验证</span>
    </div>
    <div className="overflow-hidden rounded-md border border-[#F0F0F0]">
      <div className="grid grid-cols-4 border-b border-[#F0F0F0] bg-[#F0F5FF]">
        <div className="border-r border-[#F0F0F0] p-2 text-center text-sm font-semibold text-[#1F1F1F]">
          评估指标
        </div>
        <div className="border-r border-[#F0F0F0] p-2 text-center text-sm font-semibold text-[#1F1F1F]">
          组合表现
        </div>
        <div className="border-r border-[#F0F0F0] p-2 text-center text-sm font-semibold text-[#1F1F1F]">
          客户要求
        </div>
        <div className="p-2 text-center text-sm font-semibold text-[#1F1F1F]">达标状态</div>
      </div>
      <div className="grid grid-cols-4">
        <div className="border-r border-[#F0F0F0] p-2 text-center text-xs text-[#1F1F1F]">
          近一年收益率
        </div>
        <div className="border-r border-[#F0F0F0] p-2 text-center text-xs text-[#CF1322]">
          26.11%
        </div>
        <div className="border-r border-[#F0F0F0] p-2 text-center text-xs text-[#1F1F1F]">≥10%</div>
        <div className="p-2 text-center text-xs text-[#16A34A]">超额达成</div>
      </div>
    </div>
  </div>
);

const mapLevelToHeader = (level: string) => {
  switch (level) {
    case 'R1':
      return '低风险稳健';
    case 'R2':
      return '中低风险';
    case 'R3':
      return '中风险';
    case 'R4':
      return '中高风险';
    case 'R5':
      return '高风险';
    default:
      return '未知风险';
  }
};

const ProductCard = ({ item }: { item: any }) => {
  const getHeaderStyle = () => {
    switch (item.level) {
      case 'R1':
        return 'bg-[rgba(240,253,244,0.60)] border-green-200';
      case 'R2':
        return 'bg-[linear-gradient(267deg,#EEEDFF_4.19%,#F6F7FF_86.14%)] border-blue-200';
      case 'R3':
        return 'bg-[linear-gradient(267deg,#FFF7E6_4.19%,#FFF1E6_86.14%)] border-orange-200';
      case 'R4':
        return 'bg-[linear-gradient(90deg,#FFF8FB_0%,#FFF0F6_100%)] border-red-200';
      case 'R5':
        return 'bg-[linear-gradient(90deg,#FFF8FB_0%,#FFF0F6_100%)] border-red-200';
      default:
        return 'bg-[linear-gradient(90deg,#FFF8FB_0%,#FFF0F6_100%)] border-gray-200';
    }
  };

  const getTagStyle = () => {
    const color = getRiskLevelColor(item.level);
    switch (color) {
      case 'green':
        return 'bg-green-100 text-green-800';
      case 'blue':
        return 'bg-blue-100 text-blue-800';
      case 'orange':
        return 'bg-orange-100 text-orange-800';
      case 'red':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className={`${getHeaderStyle()} flex items-center justify-between px-3 py-2`}>
        <span className="text-sm font-semibold text-[#1F1F1F]">{item.type}</span>
        <span className={`rounded px-2 py-0.5 text-xs ${getTagStyle()}`}>
          {mapLevelToHeader(item.level)}
        </span>
      </div>
      <div className="p-3">
        <div className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-[#1F1F1F]">{item.product_name}</span>
              <div className="flex gap-1">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                  {item.subtype}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-start gap-2 text-xs text-[#8C8C8C]">
              <span>建议配置比例{item.proportion}</span>
              <div className="h-3 w-px bg-[#F0F0F0]"></div>
              <span>赎回费{item.historical_return_1y}</span>
              <div className="h-3 w-px bg-[#F0F0F0]"></div>
              <span>最大回撤{item.max_drawdown_1y}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="flex items-center justify-start gap-1">
                <span className="text-base font-semibold text-[#F5222D]">
                  {item.historical_return_1y}
                </span>
              </div>
              <div className="flex items-center justify-start gap-1">
                <span className="text-xs text-[#1F1F1F]">近一年收益率</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-start gap-1">
                <span className="text-base font-semibold text-[#1F1F1F]">
                  {item.max_drawdown_1y}
                </span>
              </div>
              <div className="flex items-center justify-start gap-1">
                <span className="text-xs text-[#1F1F1F]">最大回撤</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AssetAllocation: React.FC<{ totalData: any }> = ({ totalData }) => {
  const filteredData = totalData?.filtered || [];

  // 根据类型分组并计算总比例
  const typeGroups = filteredData.reduce((acc: any, item: any) => {
    if (!acc[item.type]) {
      acc[item.type] = {
        type: item.type,
        totalProportion: 0,
        color: getTypeColor(item.type)
      };
    }
    acc[item.type].totalProportion += parseFloat(item.proportion.replace('%', ''));
    return acc;
  }, {});

  const assetData = Object.values(typeGroups).map((group: any) => ({
    name: group.type,
    value: group.totalProportion,
    color: group.color
  }));

  return (
    <div className="space-y-3">
      <HeaderSection />
      <div className="mb-2 rounded-lg bg-white p-3">
        <div className="mb-2 flex h-[150px] w-full justify-center">
          <ChartRadial
            data={assetData}
            legendPosition="right"
            innerRadius={70}
            outerRadius={40}
            cx="50%"
            cy="50%"
            legendClassName="absolute right-1/2 translate-x-[20vw] max-[500px]:translate-x-[30vw] gap-2"
            customContent={() => (
              <>
                <text
                  x="40%"
                  y="50%"
                  dy="-10"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={12}
                  fill="#1F1F1F"
                >
                  总结
                </text>
                <br />
                <text
                  x="40%"
                  y="50%"
                  dy="10"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={12}
                  fill="#1F1F1F"
                >
                  100万
                </text>
              </>
            )}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded">
          <img src={Header2} alt="header2" className="h-4 w-5" />
        </div>
        <span className="text-base font-semibold text-[#1F1F1F]">具体产品配置建议</span>
      </div>
      <PerformanceTable />
      <Summary
        isShowSuggestions={false}
        title="AI总结-投资策略"
        content={totalData?.evaluation?.evaluation || ''}
      />
      <div className="space-y-3">
        {filteredData.map((item: any, index: number) => (
          <ProductCard key={index} item={item} />
        ))}
      </div>
    </div>
  );
};

export default AssetAllocation;
